/* @jsx h */
import { defineComponent, h } from "vue";
import type { MenuCreatorProduct } from "../types";
import { NutritionalSummary } from "./NutritionalSummary";
import { useMenu } from "@app/vue/providers/index";

export interface ProductCardProps {
  product: MenuCreatorProduct;
  onEdit: (product: MenuCreatorProduct) => void;
  onDelete: (productId: string) => void;
  onEditNutrition: (product: MenuCreatorProduct) => void;
  onViewDetails: (product: MenuCreatorProduct) => void;
}

export const ProductCard = defineComponent<ProductCardProps>({
  name: "ProductCard",
  props: ["product", "onEdit", "onDelete", "onEditNutrition", "onViewDetails"],
  setup(props) {
    const { shop } = useMenu();
    const hasNutrition = () => {
      const info = props.product.nutritional_info;
      if (!info) return false;
      const macros = info.macronutrients;
      const sugar = macros.totalCarbohydrate.totalSugars;
      const sodium = macros.sodium?.value;
      return (
        Number.isFinite(info.calories) ||
        Number.isFinite(macros.totalFat.value) ||
        Number.isFinite(macros.totalCarbohydrate.value) ||
        Number.isFinite(macros.protein.value) ||
        Number.isFinite(sugar) ||
        Number.isFinite(sodium)
      );
    };

    return () => {
      const nutrition = props.product.nutritional_info;
      return (
        <div
          class="group bg-white rounded-xl border border-gray-200 overflow-hidden hover:shadow-xl hover:border-gray-300 transition-all duration-300 cursor-pointer"
          onClick={() => props.onViewDetails(props.product)}
        >
          {/* Product Image */}
          <div class="aspect-[4/3] overflow-hidden bg-gray-100 relative">
            <img
              src={
                props.product.sample_image ||
                "https://images.unsplash.com/photo-1565299624946-b28f40a0ca4b?w=400&h=300&fit=crop"
              }
              alt={props.product.name}
              class="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
            />

            {/* Nutrition Label Overlay */}
            {hasNutrition() && nutrition && (
              <div class="absolute top-3 left-3 right-3">
                <div class="rounded-2xl border border-white/40 bg-white/85 backdrop-blur-md p-3 shadow-lg">
                  <NutritionalSummary nutrition={nutrition} />
                </div>
              </div>
            )}

            {/* Action Buttons */}
            <div class="absolute top-3 right-3 opacity-0 group-hover:opacity-100 transition-opacity">
              <div class="flex gap-1">
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    props.onViewDetails(props.product);
                  }}
                  class="p-2 bg-white/90 backdrop-blur-sm rounded-full shadow-sm hover:bg-white transition-colors"
                  title="View Details"
                >
                  <span class="material-symbols-outlined text-sm text-gray-600">
                    visibility
                  </span>
                </button>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    props.onEdit(props.product);
                  }}
                  class="p-2 bg-white/90 backdrop-blur-sm rounded-full shadow-sm hover:bg-white transition-colors"
                  title="Edit"
                >
                  <span class="material-symbols-outlined text-sm text-gray-600">
                    edit
                  </span>
                </button>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    props.onEditNutrition(props.product);
                  }}
                  class="p-2 bg-white/90 backdrop-blur-sm rounded-full shadow-sm hover:bg-white transition-colors"
                  title="Nutrition Info"
                >
                  <span class="material-symbols-outlined text-sm text-gray-600">
                    {hasNutrition() ? "fact_check" : "add_circle"}
                  </span>
                </button>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    props.onDelete(props.product.id);
                  }}
                  class="p-2 bg-white/90 backdrop-blur-sm rounded-full shadow-sm hover:bg-white transition-colors"
                  title="Delete"
                >
                  <span class="material-symbols-outlined text-sm text-red-500">
                    delete
                  </span>
                </button>
              </div>
            </div>
          </div>

          <div class="p-6">
            <div class="flex items-start justify-between mb-3">
              <h4 class="font-serif font-medium text-lg text-gray-900 group-hover:text-gray-700 transition-colors">
                {props.product.name}
              </h4>
              <span class="text-xl font-semibold text-gray-900 ml-3">
                {shop.value.currency} {props.product.price}
              </span>
            </div>

            {props.product.description && (
              <p class="text-sm text-gray-600 mb-4 font-light leading-relaxed line-clamp-2">
                {props.product.description}
              </p>
            )}

            <div class="flex items-center justify-between">
              <div class="flex items-center gap-2">
                <span
                  class={`px-3 py-1 rounded-full text-xs font-medium ${
                    props.product.item_type === "food"
                      ? "bg-green-100 text-green-700"
                      : "bg-blue-100 text-blue-700"
                  }`}
                >
                  {props.product.item_type}
                </span>
                {props.product.weight && (
                  <span class="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded-full">
                    {props.product.weight}
                  </span>
                )}
              </div>
              {hasNutrition() && (
                <div class="flex items-center gap-1 text-green-600">
                  <span class="material-symbols-outlined text-sm">
                    fact_check
                  </span>
                  <span class="text-xs font-medium">Nutrition</span>
                </div>
              )}
            </div>

            {/* Nutrition badges */}
            {nutrition && (
              <div class="mt-3 flex flex-wrap gap-1">
                {nutrition.dietaryTags?.slice(0, 2).map((tag) => (
                  <span
                    key={tag}
                    class="text-xs bg-green-50 text-green-700 px-2 py-1 rounded-full"
                  >
                    {tag}
                  </span>
                ))}
                {nutrition.allergens && nutrition.allergens.length > 0 && (
                  <span class="text-xs bg-red-50 text-red-700 px-2 py-1 rounded-full">
                    Contains allergens
                  </span>
                )}
              </div>
            )}
          </div>
        </div>
      );
    };
  },
});
